import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import LoadFailedPanel from "@/components/LoadFailedPanel";
import {
  UserPlus, LockOpen, Key, UserMinus, UserCheck, PencilSimple,
  MagnifyingGlass, ArrowClockwise, PaperPlaneTilt, Copy, X, ShieldWarning,
  CheckCircle, WarningCircle, EnvelopeSimple,
} from "@phosphor-icons/react";

const DEPARTMENTS = [
  "Executive", "Finance", "Legal", "Investigations", "Consulting",
  "HR", "Quality", "Operations", "Field",
];
const TIERS = [1, 2, 3, 4, 5];

/**
 * People Admin — the single place to onboard, exit, edit and manage passwords
 * for the whole team. Replaces the scattered /settings/admin/invite-user,
 * inline reset-password endpoint, and gives a lock-status view.
 *
 * Requires ux_tier >= 4 (COO/CEO). Password reset with a custom password is
 * CEO-only (Tier 5) — the UI degrades gracefully for Tier 4.
 */
export default function AdminPeople() {
  const { user: me } = useAuth();
  const isTier4Plus = (me?.ux_tier || 0) >= 4;
  const isCEO = (me?.ux_tier || 0) >= 5;

  const [users, setUsers] = useState([]);
  const [loadErr, setLoadErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [filterDept, setFilterDept] = useState("");
  const [filterStatus, setFilterStatus] = useState(""); // "" | "active" | "inactive" | "locked" | "must_change"
  const [filterTier, setFilterTier] = useState("");
  const [modal, setModal] = useState(null); // { type: 'add' | 'edit' | 'reset' | ..., user? }
  const [toast, setToast] = useState(null); // { kind: 'ok'|'err', text }

  const load = useCallback(async () => {
    setLoadErr(null); setBusy(true);
    try {
      const { data } = await api.get("/users");
      setUsers(Array.isArray(data) ? data : data?.users || []);
    } catch (e) {
      setLoadErr(e);
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { if (isTier4Plus) load(); }, [isTier4Plus, load]);

  const notify = (kind, text) => {
    setToast({ kind, text });
    setTimeout(() => setToast(null), 4000);
  };

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return users.filter(u => {
      if (u.is_distribution_alias) return false; // aliases live in a separate module
      if (qq && !`${u.name || ""} ${u.email || ""} ${u.designation || ""}`.toLowerCase().includes(qq)) return false;
      if (filterDept && (u.department || "").toLowerCase() !== filterDept.toLowerCase()) return false;
      if (filterTier && String(u.ux_tier || "") !== String(filterTier)) return false;
      if (filterStatus) {
        const st = statusOf(u);
        if (st.key !== filterStatus) return false;
      }
      return true;
    });
  }, [users, q, filterDept, filterStatus, filterTier]);

  const byStatus = useMemo(() => {
    const c = { active: 0, inactive: 0, locked: 0, must_change: 0 };
    users.forEach(u => { if (u.is_distribution_alias) return; c[statusOf(u).key] = (c[statusOf(u).key] || 0) + 1; });
    return c;
  }, [users]);

  if (!isTier4Plus) return (
    <div className="p-6" data-testid="people-gate">
      <div className="card-flat p-6 border border-amber-200 bg-amber-50 text-amber-900 text-[13px]">
        People Admin requires Tier 4+ (COO or CEO). Contact your administrator.
      </div>
    </div>
  );

  if (loadErr) return (
    <div className="p-6"><LoadFailedPanel error={loadErr} resource="Team roster" onRetry={load} /></div>
  );

  return (
    <div className="p-6 space-y-4 max-w-[1400px]" data-testid="admin-people-page">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow">Admin · People</div>
          <h1 className="font-display text-[28px] font-bold tracking-tight mt-1">Team members</h1>
          <div className="text-[12px] text-[var(--text-muted)] mt-1">
            Onboard, edit, exit, and manage passwords for every account — one screen, no digging.
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {isCEO && (
            <button data-testid="people-open-migrations"
              onClick={async () => {
                try {
                  const { data } = await api.get("/admin/migrate/status");
                  const summary =
                    `📊 Current DB state:\n\n` +
                    `Clients: ${data.clients.primary} primary` +
                    `${data.clients.migration_needed ? " ⚠️ needs migration (expected 24)" : " ✓"}\n` +
                    `Vendors: ${data.vendors.primary} primary` +
                    `${data.vendors.migration_needed ? " ⚠️ needs migration (expected 14)" : " ✓"}\n` +
                    `Invoices: ${data.invoices.from_master_sheet} from master sheet` +
                    `${data.invoices.migration_needed ? " ⚠️ needs migration (expected 198)" : " ✓"}\n\n` +
                    `Click OK to run ALL migrations that need it (safe & idempotent). Cancel to abort.`;
                  if (!window.confirm(summary)) return;
                  const results = [];
                  if (data.clients.migration_needed) {
                    notify("ok", "Running client master sheet migration…");
                    const r = await api.post("/admin/migrate/clients");
                    results.push(`Clients: ${r.data.total_primary} primary (${r.data.soft_deleted} legacy hidden)`);
                  }
                  if (data.vendors.migration_needed) {
                    notify("ok", "Running vendor master list migration…");
                    const r = await api.post("/admin/migrate/vendors");
                    results.push(`Vendors: ${r.data.total_primary} primary (${r.data.soft_deleted} legacy hidden)`);
                  }
                  if (data.invoices.migration_needed) {
                    notify("ok", "Running invoicing tracker migration (this can take 20s)…");
                    const r = await api.post("/admin/migrate/invoices");
                    results.push(`Invoices: ${r.data.invoices_imported} imported, ${r.data.placeholder_clients_created} placeholder clients`);
                  }
                  if (results.length === 0) { notify("ok", "All migrations already applied ✓"); }
                  else { notify("ok", "Done!\n" + results.join("\n")); }
                  load();
                } catch (e) { notify("err", formatApiError(e.response?.data?.detail) || "Migration failed."); }
              }}
              className="span-btn span-btn-ghost h-9 text-[12px] inline-flex items-center gap-1.5">
              <ArrowClockwise size={13} /> Run data migrations
            </button>
          )}
          {isCEO && (
            <button data-testid="people-download-ceo-ref"
              onClick={async () => {
                try {
                  const r = await api.get("/admin/ceo-reference.pdf", { responseType: "blob" });
                  const a = document.createElement("a");
                  a.href = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
                  a.download = "SPAN_CEO_Master_Reference.pdf";
                  document.body.appendChild(a); a.click(); a.remove();
                  notify("ok", "CEO Master Reference downloaded.");
                } catch (e) { notify("err", formatApiError(e.response?.data?.detail) || "Download failed."); }
              }}
              className="span-btn span-btn-ghost h-9 text-[12px] inline-flex items-center gap-1.5">
              <PaperPlaneTilt size={13} /> CEO reference PDF
            </button>
          )}
          <Link to="/admin/onboarding" data-testid="people-bulk-onboarding"
            className="span-btn span-btn-ghost h-9 text-[12px] inline-flex items-center gap-1.5">
            <PaperPlaneTilt size={13} /> Bulk onboarding blast
          </Link>
          <button data-testid="people-add" onClick={() => setModal({ type: "add" })}
            className="span-btn h-9 text-[12px] inline-flex items-center gap-1.5">
            <UserPlus size={13} /> Add team member
          </button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <KPI label="Active" count={byStatus.active || 0} tone="ok" testid="kpi-active" onClick={() => setFilterStatus("active")} />
        <KPI label="Locked" count={byStatus.locked || 0} tone="bad" testid="kpi-locked" onClick={() => setFilterStatus("locked")} />
        <KPI label="Must change pw" count={byStatus.must_change || 0} tone="warn" testid="kpi-must-change" onClick={() => setFilterStatus("must_change")} />
        <KPI label="Deactivated" count={byStatus.inactive || 0} tone="mute" testid="kpi-inactive" onClick={() => setFilterStatus("inactive")} />
      </div>

      {/* Toolbar */}
      <div className="card-flat p-3 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1.5 border border-[var(--border)] rounded px-2 h-8 bg-white flex-1 min-w-[220px]">
          <MagnifyingGlass size={13} className="text-[var(--text-muted)]" />
          <input type="text" placeholder="Search name, email, designation…"
            value={q} onChange={e => setQ(e.target.value)}
            data-testid="people-search"
            className="outline-none text-[12px] flex-1" />
        </div>
        <select value={filterDept} onChange={e => setFilterDept(e.target.value)}
          data-testid="people-filter-dept"
          className="border border-[var(--border)] rounded px-2 h-8 text-[12px] bg-white">
          <option value="">All departments</option>
          {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        <select value={filterTier} onChange={e => setFilterTier(e.target.value)}
          data-testid="people-filter-tier"
          className="border border-[var(--border)] rounded px-2 h-8 text-[12px] bg-white">
          <option value="">All tiers</option>
          {TIERS.map(t => <option key={t} value={t}>Tier {t}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          data-testid="people-filter-status"
          className="border border-[var(--border)] rounded px-2 h-8 text-[12px] bg-white">
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="locked">Locked</option>
          <option value="must_change">Must change password</option>
          <option value="inactive">Deactivated</option>
        </select>
        <button onClick={() => { setQ(""); setFilterDept(""); setFilterTier(""); setFilterStatus(""); }}
          data-testid="people-clear-filters"
          className="text-[11px] text-[var(--text-muted)] hover:text-[var(--brand-primary)]">
          Clear
        </button>
        <button onClick={load} disabled={busy}
          data-testid="people-refresh"
          className="span-btn span-btn-ghost h-8 text-[11px] inline-flex items-center gap-1 ml-auto">
          <ArrowClockwise size={12} className={busy ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {/* Table */}
      <div className="card-flat overflow-x-auto" data-testid="people-table-wrap">
        <table className="w-full text-[12px]">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] border-b border-[var(--border)]">
              <Th>Name</Th>
              <Th>Email</Th>
              <Th>Department</Th>
              <Th>Tier</Th>
              <Th>Status</Th>
              <Th>Last activity</Th>
              <Th className="text-right pr-3">Actions</Th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="text-center py-6 text-[var(--text-muted)]">
                No users match the current filters.
              </td></tr>
            )}
            {filtered.map(u => {
              const st = statusOf(u);
              return (
                <tr key={u.user_id} data-testid={`people-row-${u.user_id}`}
                    className="border-b border-[var(--border)] hover:bg-[var(--surface-2,#f9fafb)]">
                  <Td>
                    <div className="font-semibold">{u.name || "—"}</div>
                    <div className="text-[10px] text-[var(--text-muted)]">{u.designation || u.role || "—"}</div>
                  </Td>
                  <Td className="font-mono text-[11px]">{u.email}</Td>
                  <Td>{u.department || "—"}</Td>
                  <Td><span className="font-mono">T{u.ux_tier || 1}</span></Td>
                  <Td><StatusChip status={st} /></Td>
                  <Td className="text-[10px] text-[var(--text-muted)]">
                    {u.last_login_at ? fmtDate(u.last_login_at) : (u.password_rotated_at ? `pw · ${fmtDate(u.password_rotated_at)}` : "—")}
                  </Td>
                  <Td className="text-right pr-3">
                    <div className="inline-flex gap-1 flex-wrap justify-end">
                      <IconBtn testid={`people-reset-${u.user_id}`} title="Reset password" onClick={() => setModal({ type: "reset", user: u })}>
                        <Key size={13} />
                      </IconBtn>
                      {st.key === "locked" && (
                        <IconBtn testid={`people-unlock-${u.user_id}`} title="Unlock account" tone="warn" onClick={() => doUnlock(u, load, notify)}>
                          <LockOpen size={13} />
                        </IconBtn>
                      )}
                      <IconBtn testid={`people-edit-${u.user_id}`} title="Edit member" onClick={() => setModal({ type: "edit", user: u })}>
                        <PencilSimple size={13} />
                      </IconBtn>
                      {u.active === false ? (
                        <IconBtn testid={`people-reactivate-${u.user_id}`} title="Reactivate" tone="ok" onClick={() => doReactivate(u, load, notify)}>
                          <UserCheck size={13} />
                        </IconBtn>
                      ) : (
                        <IconBtn testid={`people-deactivate-${u.user_id}`} title="Deactivate (exit)" tone="bad" onClick={() => doDeactivate(u, load, notify)}>
                          <UserMinus size={13} />
                        </IconBtn>
                      )}
                    </div>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Modals */}
      {modal?.type === "add" && (
        <AddUserModal onClose={() => setModal(null)} onDone={() => { setModal(null); load(); }} notify={notify} />
      )}
      {modal?.type === "edit" && (
        <EditUserModal user={modal.user} onClose={() => setModal(null)} onDone={() => { setModal(null); load(); }} notify={notify} />
      )}
      {modal?.type === "reset" && (
        <ResetPasswordModal user={modal.user} isCEO={isCEO}
          onClose={() => setModal(null)}
          onDone={() => { setModal(null); load(); }}
          notify={notify} />
      )}

      {/* Toast */}
      {toast && (
        <div data-testid="people-toast"
          className={`fixed bottom-6 right-6 max-w-[420px] px-4 py-2.5 rounded shadow-lg text-[12px] flex items-start gap-2 z-[300]
            ${toast.kind === "ok" ? "bg-emerald-600 text-white" : "bg-rose-700 text-white"}`}>
          {toast.kind === "ok" ? <CheckCircle size={16} className="mt-0.5" /> : <WarningCircle size={16} className="mt-0.5" />}
          <div className="whitespace-pre-wrap">{toast.text}</div>
        </div>
      )}
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function statusOf(u) {
  if (u.active === false) return { key: "inactive", label: "Deactivated", tone: "mute" };
  const lockedUntil = u.locked_until && new Date(u.locked_until) > new Date();
  if (lockedUntil || (u.failed_login_attempts || 0) >= 5) return { key: "locked", label: "Locked", tone: "bad" };
  if (u.must_change_password) return { key: "must_change", label: "Must change pw", tone: "warn" };
  return { key: "active", label: "Active", tone: "ok" };
}

function fmtDate(iso) {
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" }); }
  catch { return "—"; }
}

const Th = ({ children, className = "" }) => (
  <th className={`text-left py-2 px-3 font-semibold ${className}`}>{children}</th>
);
const Td = ({ children, className = "" }) => (
  <td className={`py-2 px-3 align-top ${className}`}>{children}</td>
);

function KPI({ label, count, tone, onClick, testid }) {
  const tones = {
    ok:   "border-emerald-200 bg-emerald-50 text-emerald-900",
    bad:  "border-rose-200 bg-rose-50 text-rose-900",
    warn: "border-amber-200 bg-amber-50 text-amber-900",
    mute: "border-[var(--border)] bg-[var(--surface-2,#f9fafb)] text-[var(--text-2)]",
  };
  return (
    <button data-testid={testid} onClick={onClick}
      className={`p-3 border rounded text-left hover:brightness-95 transition ${tones[tone]}`}>
      <div className="text-[10px] uppercase tracking-wider opacity-80">{label}</div>
      <div className="text-[22px] font-bold font-display leading-none mt-0.5">{count}</div>
    </button>
  );
}

function StatusChip({ status }) {
  const tones = {
    ok:   "bg-emerald-50 text-emerald-800 border-emerald-200",
    bad:  "bg-rose-50 text-rose-800 border-rose-200",
    warn: "bg-amber-50 text-amber-900 border-amber-200",
    mute: "bg-gray-100 text-gray-700 border-gray-200",
  };
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 text-[10px] uppercase tracking-wider rounded border ${tones[status.tone]}`}>
      {status.label}
    </span>
  );
}

function IconBtn({ children, onClick, title, tone = "default", testid }) {
  const tones = {
    default: "border-[var(--border)] hover:bg-[var(--surface-2,#f9fafb)]",
    ok: "border-emerald-200 text-emerald-700 hover:bg-emerald-50",
    bad: "border-rose-200 text-rose-700 hover:bg-rose-50",
    warn: "border-amber-200 text-amber-700 hover:bg-amber-50",
  };
  return (
    <button data-testid={testid} onClick={onClick} title={title}
      className={`inline-flex items-center justify-center w-7 h-7 border rounded ${tones[tone]}`}>
      {children}
    </button>
  );
}

// ─── Actions ────────────────────────────────────────────────────────────────

async function doUnlock(u, load, notify) {
  if (!window.confirm(`Unlock ${u.name || u.email}?\nClears failed-attempt counter and lock timer. Password is NOT changed.`)) return;
  try {
    await api.post("/admin/unlock-user", { email: u.email });
    notify("ok", `${u.name || u.email} unlocked.`);
    load();
  } catch (e) {
    notify("err", formatApiError(e.response?.data?.detail) || "Unlock failed.");
  }
}

async function doDeactivate(u, load, notify) {
  if (!window.confirm(
    `Deactivate ${u.name || u.email}?\n\n` +
    "• They will no longer be able to log in.\n" +
    "• Their record is moved to the Recycle Bin (auto-purge after 730 days).\n" +
    "• All their case assignments remain intact for the audit trail.\n\n" +
    "You can reactivate anytime."
  )) return;
  try {
    await api.delete(`/admin/users/${u.user_id}`);
    notify("ok", `${u.name || u.email} deactivated.`);
    load();
  } catch (e) {
    notify("err", formatApiError(e.response?.data?.detail) || "Deactivate failed.");
  }
}

async function doReactivate(u, load, notify) {
  try {
    await api.put(`/admin/users/${u.user_id}`, { active: true });
    notify("ok", `${u.name || u.email} reactivated.`);
    load();
  } catch (e) {
    notify("err", formatApiError(e.response?.data?.detail) || "Reactivate failed.");
  }
}

// ─── Modals ─────────────────────────────────────────────────────────────────

function ModalShell({ title, onClose, children, testid, width = "max-w-md" }) {
  return (
    <div className="fixed inset-0 z-[250] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid={testid} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`bg-white rounded shadow-2xl w-full ${width} max-h-[90vh] overflow-y-auto`}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--border)]">
          <div className="font-semibold text-[13px]">{title}</div>
          <button onClick={onClose} data-testid="modal-close" className="p-1 hover:bg-gray-100 rounded">
            <X size={14} />
          </button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

function AddUserModal({ onClose, onDone, notify }) {
  const [form, setForm] = useState({
    email: "", name: "", department: "Investigations",
    ux_tier: 2, designation: "Team Member", role: "Investigator",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (!form.email || !form.name) { setErr("Email and name are required."); return; }
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/admin/users/invite", form);
      setResult(data);
      notify("ok", `${form.name} invited. Temp password shown below.`);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Invite failed.");
    } finally { setBusy(false); }
  };

  return (
    <ModalShell title="Add team member" onClose={onClose} testid="add-user-modal" width="max-w-lg">
      {!result ? (
        <div className="space-y-3">
          <TxtField label="Email" testid="add-email" value={form.email}
            onChange={v => setForm({ ...form, email: v })} placeholder="person@spanip.com" />
          <TxtField label="Full name" testid="add-name" value={form.name}
            onChange={v => setForm({ ...form, name: v })} placeholder="Full Name" />
          <div className="grid grid-cols-2 gap-2">
            <SelField label="Department" testid="add-dept" value={form.department}
              options={DEPARTMENTS} onChange={v => setForm({ ...form, department: v })} />
            <SelField label="UX Tier" testid="add-tier" value={form.ux_tier}
              options={TIERS} onChange={v => setForm({ ...form, ux_tier: parseInt(v, 10) })} />
          </div>
          <TxtField label="Designation" testid="add-designation" value={form.designation}
            onChange={v => setForm({ ...form, designation: v })} />
          <TxtField label="Role" testid="add-role" value={form.role}
            onChange={v => setForm({ ...form, role: v })} />
          {err && <div className="text-[12px] text-rose-700 bg-rose-50 border border-rose-200 px-2 py-1.5 rounded">{err}</div>}
          <div className="flex gap-2 justify-end pt-2">
            <button onClick={onClose} className="span-btn span-btn-ghost h-8 text-[12px]">Cancel</button>
            <button onClick={submit} disabled={busy} data-testid="add-submit"
              className="span-btn h-8 text-[12px] inline-flex items-center gap-1.5 disabled:opacity-60">
              <UserPlus size={13} /> {busy ? "Inviting…" : "Create & send invite"}
            </button>
          </div>
        </div>
      ) : (
        <InviteResult result={result} onDone={onDone} notify={notify} />
      )}
    </ModalShell>
  );
}

function InviteResult({ result, onDone, notify }) {
  return (
    <div className="space-y-3" data-testid="add-user-result">
      <div className="bg-emerald-50 border border-emerald-200 text-emerald-900 px-3 py-2.5 rounded text-[12px]">
        <div className="font-semibold flex items-center gap-1.5"><CheckCircle size={15} /> Invite created</div>
        <div className="mt-1">Email: <strong className="font-mono">{result.email}</strong></div>
        <div>Email invite sent: <strong>{result.invite_email_sent ? "yes" : "no (email not configured)"}</strong></div>
      </div>
      <div className="p-3 border border-[var(--border)] rounded bg-[var(--surface-2,#f9fafb)]">
        <div className="text-[11px] text-[var(--text-muted)] mb-1">Temporary password (share securely — they will be forced to change on first login):</div>
        <div className="flex items-center gap-2">
          <code className="font-mono text-[13px] font-bold text-[var(--brand-primary)] break-all flex-1" data-testid="add-user-temp-pw">
            {result.temp_password}
          </code>
          <button onClick={() => { navigator.clipboard.writeText(result.temp_password); notify("ok", "Password copied."); }}
            data-testid="add-user-copy-pw"
            className="span-btn span-btn-ghost h-7 text-[11px] inline-flex items-center gap-1">
            <Copy size={12} /> Copy
          </button>
        </div>
      </div>
      <div className="flex justify-end">
        <button onClick={onDone} className="span-btn h-8 text-[12px]" data-testid="add-user-done">Done</button>
      </div>
    </div>
  );
}

function EditUserModal({ user: u, onClose, onDone, notify }) {
  const [form, setForm] = useState({
    name: u.name || "", department: u.department || "Investigations",
    designation: u.designation || "", role: u.role || "",
    ux_tier: u.ux_tier || 2,
    reports_to: u.reports_to || "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [roster, setRoster] = useState([]);

  // Load a lean roster so the CEO can pick a new reporting manager
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/users");
        const list = Array.isArray(data) ? data : data?.users || [];
        if (!cancelled) setRoster(list.filter(x => !x.is_distribution_alias && x.user_id !== u.user_id && x.active !== false));
      } catch { /* silent — reports_to becomes free-text if roster fails */ }
    })();
    return () => { cancelled = true; };
  }, [u.user_id]);

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      // Only include fields that actually changed — avoids clobbering un-set values
      const payload = {};
      if (form.name !== (u.name || "")) payload.name = form.name;
      if (form.department !== (u.department || "")) payload.department = form.department;
      if (form.designation !== (u.designation || "")) payload.designation = form.designation;
      if (form.role !== (u.role || "")) payload.role = form.role;
      if (parseInt(form.ux_tier, 10) !== (u.ux_tier || 0)) payload.ux_tier = parseInt(form.ux_tier, 10);
      if (form.reports_to !== (u.reports_to || "")) payload.reports_to = form.reports_to;
      if (Object.keys(payload).length === 0) { onDone(); return; }
      await api.put(`/admin/users/${u.user_id}`, payload);
      notify("ok", `${form.name || u.email} updated. Changes will persist across restarts.`);
      onDone();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Update failed.");
    } finally { setBusy(false); }
  };

  return (
    <ModalShell title={`Edit ${u.name || u.email}`} onClose={onClose} testid="edit-user-modal" width="max-w-lg">
      <div className="space-y-3">
        <div className="text-[11px] text-[var(--text-muted)] font-mono">{u.email}</div>
        <TxtField label="Full name" testid="edit-name" value={form.name}
          onChange={v => setForm({ ...form, name: v })} />
        <div className="grid grid-cols-2 gap-2">
          <SelField label="Department" testid="edit-dept" value={form.department}
            options={DEPARTMENTS} onChange={v => setForm({ ...form, department: v })} />
          <SelField label="UX Tier" testid="edit-tier" value={form.ux_tier}
            options={TIERS} onChange={v => setForm({ ...form, ux_tier: parseInt(v, 10) })} />
        </div>
        <TxtField label="Designation" testid="edit-designation" value={form.designation}
          onChange={v => setForm({ ...form, designation: v })} />
        <TxtField label="Role" testid="edit-role" value={form.role}
          onChange={v => setForm({ ...form, role: v })} />
        <label className="block">
          <div className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider mb-0.5">Reports to (manager)</div>
          <select value={form.reports_to || ""} onChange={e => setForm({ ...form, reports_to: e.target.value })}
            data-testid="edit-reports-to"
            className="w-full border border-[var(--border)] rounded h-8 px-2 text-[12px] bg-white">
            <option value="">— No manager (top of org) —</option>
            {roster.map(r => (
              <option key={r.user_id} value={r.user_id}>
                {r.name} · {r.designation || r.role || `Tier ${r.ux_tier}`} · {r.email}
              </option>
            ))}
          </select>
        </label>
        <div className="text-[10px] text-[var(--text-muted)] flex items-start gap-1">
          <span className="text-emerald-700 font-semibold">✓</span>
          <span>Your edits stay permanent — they survive backend restarts and are only overwritten when YOU edit them again.</span>
        </div>
        {err && <div className="text-[12px] text-rose-700 bg-rose-50 border border-rose-200 px-2 py-1.5 rounded">{err}</div>}
        <div className="flex gap-2 justify-end pt-2">
          <button onClick={onClose} className="span-btn span-btn-ghost h-8 text-[12px]">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="edit-submit"
            className="span-btn h-8 text-[12px] disabled:opacity-60">
            {busy ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

function ResetPasswordModal({ user: u, isCEO, onClose, onDone, notify }) {
  // Default to the safer "auto & email" flow even for CEO — reduces the chance
  // of pasting a real password into chat or clipboard by accident.
  const [mode, setMode] = useState("auto"); // 'custom' | 'auto'
  const [customPw, setCustomPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      if (mode === "custom") {
        // CEO-only endpoint: /admin/reset-password/{email} with new_password
        if (customPw.length < 8) { setErr("Password must be at least 8 characters."); setBusy(false); return; }
        const { data } = await api.post(`/admin/reset-password/${encodeURIComponent(u.email)}`, { new_password: customPw });
        setResult({ password: data.new_password, emailed: false });
        notify("ok", `Password for ${u.name || u.email} reset.`);
      } else {
        // Auto-regenerate + email flow: resend-invite generates fresh temp + emails via Resend
        const { data } = await api.post(`/admin/users/${u.user_id}/resend-invite`);
        setResult({ password: data.temp_password, emailed: data.invite_email_sent });
        notify("ok", `Password for ${u.name || u.email} rotated${data.invite_email_sent ? " and emailed" : ""}.`);
      }
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Reset failed.");
    } finally { setBusy(false); }
  };

  return (
    <ModalShell title={`Reset password · ${u.name || u.email}`} onClose={onClose} testid="reset-pw-modal" width="max-w-lg">
      {!result ? (
        <div className="space-y-3">
          <div className="text-[12px] text-[var(--text-muted)] flex items-start gap-1.5">
            <ShieldWarning size={14} className="mt-0.5" />
            <span>Any active sessions for this user will continue until they sign out, but they will be forced to change their password on next login.</span>
          </div>
          <div className="border border-[var(--border)] rounded p-2 space-y-1.5">
            {isCEO && (
              <label className="flex items-start gap-2 text-[12px] cursor-pointer">
                <input type="radio" checked={mode === "custom"} onChange={() => setMode("custom")}
                  data-testid="reset-mode-custom" className="mt-1" />
                <div>
                  <div className="font-semibold">Set a specific password</div>
                  <div className="text-[10px] text-[var(--text-muted)]">CEO only. You type it, they must change it after login.</div>
                </div>
              </label>
            )}
            <label className="flex items-start gap-2 text-[12px] cursor-pointer">
              <input type="radio" checked={mode === "auto"} onChange={() => setMode("auto")}
                data-testid="reset-mode-auto" className="mt-1" />
              <div>
                <div className="font-semibold">Auto-generate & email</div>
                <div className="text-[10px] text-[var(--text-muted)]">Fresh temporary password is generated, emailed via Resend, shown here as a fallback.</div>
              </div>
            </label>
          </div>
          {mode === "custom" && (
            <TxtField label="New password (min 8 chars)" testid="reset-custom-pw"
              value={customPw} onChange={setCustomPw} type="text" mono />
          )}
          {err && <div className="text-[12px] text-rose-700 bg-rose-50 border border-rose-200 px-2 py-1.5 rounded">{err}</div>}
          <div className="flex gap-2 justify-end pt-2">
            <button onClick={onClose} className="span-btn span-btn-ghost h-8 text-[12px]">Cancel</button>
            <button onClick={submit} disabled={busy} data-testid="reset-submit"
              className="span-btn h-8 text-[12px] inline-flex items-center gap-1.5 disabled:opacity-60">
              <Key size={13} /> {busy ? "Resetting…" : "Reset password"}
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-3" data-testid="reset-result">
          <div className="bg-emerald-50 border border-emerald-200 text-emerald-900 px-3 py-2.5 rounded text-[12px]">
            <div className="font-semibold flex items-center gap-1.5">
              <CheckCircle size={15} /> Password reset for {u.email}
            </div>
            <div className="mt-1">
              {result.emailed
                ? <><EnvelopeSimple size={12} className="inline mr-1" />Email delivered via Resend.</>
                : "Email not sent (Resend unavailable) — share the password securely."}
            </div>
          </div>
          <div className="p-3 border border-[var(--border)] rounded bg-[var(--surface-2,#f9fafb)]">
            <div className="text-[11px] text-[var(--text-muted)] mb-1">New temporary password:</div>
            <div className="flex items-center gap-2">
              <code className="font-mono text-[13px] font-bold text-[var(--brand-primary)] break-all flex-1"
                data-testid="reset-result-pw">
                {result.password}
              </code>
              <button onClick={() => { navigator.clipboard.writeText(result.password); notify("ok", "Password copied."); }}
                data-testid="reset-result-copy"
                className="span-btn span-btn-ghost h-7 text-[11px] inline-flex items-center gap-1">
                <Copy size={12} /> Copy
              </button>
            </div>
          </div>
          <div className="flex justify-end">
            <button onClick={onDone} className="span-btn h-8 text-[12px]" data-testid="reset-done">Done</button>
          </div>
        </div>
      )}
    </ModalShell>
  );
}

// ─── Inputs ─────────────────────────────────────────────────────────────────

function TxtField({ label, value, onChange, placeholder, testid, type = "text", mono = false }) {
  return (
    <label className="block">
      <div className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider mb-0.5">{label}</div>
      <input type={type} value={value} onChange={e => onChange(e.target.value)}
        placeholder={placeholder} data-testid={testid}
        className={`w-full border border-[var(--border)] rounded h-8 px-2 text-[12px] ${mono ? "font-mono" : ""}`} />
    </label>
  );
}

function SelField({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <div className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider mb-0.5">{label}</div>
      <select value={value} onChange={e => onChange(e.target.value)}
        data-testid={testid}
        className="w-full border border-[var(--border)] rounded h-8 px-2 text-[12px] bg-white">
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}
